import { useEffect, useState } from 'react'
import { useAppDispatch } from '../../reducers/store.ts'
import { fetchDevicePolicy, postCreateDevicePolicy, setDevicePolicy } from '../../reducers/settings/index.ts'
import { useParams, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useDispatch } from 'react-redux'
import PolicyActions from './PolicyActions.tsx'
import PolicyDetails from './PolicyDetails.tsx'
import PolicySummary from './PolicySummary.tsx'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import PolicyFooter from './CreatePolicyFooter.tsx'
import CreatePolicyRules from './CreatePolicyRules.tsx'
import { DEFAULT_TOAST_CONFIG } from '../toast/consts.ts'
import { CreatePolicyInput, defaultDevicePolicy, PolicyActionsInput, PolicyConfig } from '../../types/SettingsType.ts'
import { createPolicySchema } from '../../schemas/IntegrationSchema.ts'
import { DeviceTravelPolicy, TRAVEL_POLICY_PERIOD, TRAVEL_RISK_LEVEL } from '../../types/TravelTypes.ts'
import { updateTravelPolicy } from '../../reducers/travel/travel.thunk.ts'
import PolicyGroups from './PolicyGroups.tsx'
import { SelectedGroup, UpdateGroupsForPoliciesRequest } from '../../types/GroupTypes.ts'
import { getUserGroups, updateGroupPolicies } from '../../reducers/groups/groups.thunk.ts'
import { useSelector } from 'react-redux'
import { selectGroup } from '../../reducers/groups/groups.slice.ts'

function CreatePolicyWrapper() {
  const di = useAppDispatch()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { userGroups } = useSelector(selectGroup)
  const { policyId } = useParams()
  const [selectedPage, setSelectedPage] = useState<string>('details')
  const [isNextEnabled, setIsNextEnabled] = useState(false)
  const [policyRules, setPolicyRules] = useState<Partial<PolicyConfig>>({})
  const [policyDetails, setPolicyDetails] = useState<CreatePolicyInput | undefined>(undefined)
  const [policyActionDetails, setPolicyActionDetails] = useState<PolicyActionsInput | undefined>(undefined)
  const [activateOnCreate, setActivateOnCreate] = useState(false)
  const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>({})
  const [selectedGroups, setSelectedGroups] = useState<SelectedGroup[]>([])

  useEffect(() => {
    di(getUserGroups())
  }, [])

  const methods = useForm<CreatePolicyInput>({
    resolver: yupResolver(createPolicySchema),
    mode: 'onChange',
    defaultValues: { name: '', description: '' },
  })

  const { handleSubmit } = methods

  const onSubmit = async (data: CreatePolicyInput) => {
    setPolicyDetails(data)
    setSelectedPage('rules')
  }

  const savePolicyRules = () => {
    setSelectedPage('integration')
  }

  useEffect(() => {
    if (policyId) {
      const numericId = Number(policyId)
      if (!isNaN(numericId)) {
        di(fetchDevicePolicy(numericId))
      } else {
        toast.error('The policy ID provided was invalid, please try again.', DEFAULT_TOAST_CONFIG)
        navigate('/device-policies')
      }
    }
  }, [policyId])

  useEffect(() => {
    return () => {
      dispatch(setDevicePolicy(undefined))
    }
  }, [])

  const onNextAction = () => {
    if (selectedPage === 'details') {
      handleSubmit(onSubmit)()
    } else if (selectedPage === 'rules') {
      savePolicyRules()
    } else if (selectedPage === 'integration') {
      setSelectedPage('groups')
    } else if (selectedPage === 'groups') {
      setSelectedPage('summary')
    } else if (selectedPage === 'summary') {
      saveCompletePolicy()
    }
  }

  const saveCompletePolicy = async () => {
    if (!policyDetails) {
      toast.error('Missing policy details', DEFAULT_TOAST_CONFIG)
      return
    }

    let actionsName = policyActionDetails?.integrationName
    if (actionsName === 'Microsoft Intune Mobile Threat Defence') {
      actionsName = 'Intune MTD'
    } else {
      actionsName = actionsName ?? 'None'
    }
    const payload = {
      ...defaultDevicePolicy,
      name: policyDetails.name,
      description: policyDetails.description ?? '',
      actions: actionsName,
      actionAssigned: true,
      gracePeriod:
        policyActionDetails?.gracePeriodValue && policyActionDetails?.gracePeriodUnit
          ? `${policyActionDetails.gracePeriodValue}_${policyActionDetails.gracePeriodUnit}`
          : null,
      active: activateOnCreate,
      threatLevel: policyActionDetails?.threatLevel ?? 'LOW',
      alertSeverity: policyRules.alertSeverity ?? 'NONE',
      deviceIntegrityPeriod: policyRules.deviceIntegrityPeriod ?? 'NEVER',
      screenLockEnabled: String(policyRules.screenLockEnabled ?? false),
      smishingEnabled: String(policyRules.smishingEnabled ?? false),
      biometricsEnabled: String(policyRules.biometricsEnabled ?? false),
      travelEnabled: String(policyRules.travelEnabled ?? false),
      iosMinimumVersion: policyRules.iosMinimumVersion,
      iosVersionOffset: policyRules.iosVersionOffset ? policyRules.iosVersionOffset.toString() : undefined,
      iosMinimumVersionGracePeriod: policyRules.iosMinimumVersionGracePeriod
        ? policyRules.iosMinimumVersionGracePeriod.toString()
        : undefined,
      iosMinimumVersionGracePeriodUnit: policyRules.iosMinimumVersionGracePeriodUnit,
      androidMinimumVersion: policyRules.androidMinimumVersion
        ? policyRules.androidMinimumVersion?.toString()
        : undefined,
      androidLastPatchVersionGracePeriod: policyRules.androidLastPatchVersionGracePeriod
        ? policyRules.androidLastPatchVersionGracePeriod.toString()
        : undefined,
      androidLastPatchVersionGracePeriodUnit: policyRules.androidLastPatchVersionGracePeriodUnit,
    }

    const created = await di(postCreateDevicePolicy(payload))

    if (created?.policyId) {
      const policyPayload: UpdateGroupsForPoliciesRequest = {
        policyId: Number(created.policyId),
        updatedGroups: Object.entries(checkedItems).map(([groupId, enabled]) => ({
          groupId: Number(groupId),
          enabled,
        })),
      }
      await di(updateGroupPolicies(policyPayload))
      if ((policyRules?.travelEnabled ?? false) === true) {
        const travelPayload: DeviceTravelPolicy = {
          devicePolicyId: Number(created.policyId),
          riskLevel: (policyRules?.travelRiskLevel ?? TRAVEL_RISK_LEVEL.NONE) as TRAVEL_RISK_LEVEL,
          riskPeriod: (policyRules?.travelRiskPeriod ?? TRAVEL_POLICY_PERIOD.SEVEN_DAYS) as TRAVEL_POLICY_PERIOD,
          sensitiveTravelRequestLocation: policyRules?.travelEnabled ?? false,
        }
        const travelPolicyUpdated = await di(updateTravelPolicy(travelPayload))
        if (travelPolicyUpdated) {
          toast.success('Policy created successfully', DEFAULT_TOAST_CONFIG)
          navigate('/device-policies')
          return
        } else {
          toast.error('Failed to create policy', DEFAULT_TOAST_CONFIG)
          return
        }
      }
      toast.success('Policy created successfully', DEFAULT_TOAST_CONFIG)
      navigate('/device-policies')
    } else {
      toast.error('Failed to create policy', DEFAULT_TOAST_CONFIG)
    }
  }

  useEffect(() => {
    if (!userGroups) return

    const selected: SelectedGroup[] = Object.entries(checkedItems)
      .filter(([_, enabled]) => enabled)
      .map(([groupId]) => {
        const group = userGroups.find((g) => g.id === Number(groupId))
        return group
          ? {
              name: group.name,
              memberCount: group.memberCount,
            }
          : null
      })
      .filter((g): g is SelectedGroup => g !== null)

    setSelectedGroups(selected)
  }, [checkedItems, userGroups])

  return (
    <FormProvider {...methods}>
      <div className="absolute top-18 left-52 rounded-tl-lg border-mainBorder border-t border-l bg-contentBackground w-[calc(100%_-_208px)] h-[calc(100%_-_74px)] overflow-auto">
        <div className="h-12 w-full border-b border-mainBorder flex gap-6 p-4 mb-10 bg-white">
          <div>
            <p className={`${selectedPage === 'details' ? 'text-mainText' : 'text-textTertiary'} text-sm`}>
              1. Details
            </p>
            {selectedPage === 'details' && <div className="h-0.5 bg-mainText rounded-t-lg mt-2.5" />}
          </div>
          <div>
            <p className={`${selectedPage === 'rules' ? 'text-mainText' : 'text-textTertiary'} text-sm`}>
              2. Add Rules
            </p>
            {selectedPage === 'rules' && <div className="h-0.5 bg-mainText rounded-t-lg mt-2.5" />}
          </div>
          <div>
            <p className={`${selectedPage === 'integration' ? 'text-mainText' : 'text-textTertiary'} text-sm`}>
              3. Set Integration
            </p>
            {selectedPage === 'integration' && <div className="h-0.5 bg-mainText rounded-t-lg mt-2.5" />}
          </div>
          <div>
            <p className={`${selectedPage === 'groups' ? 'text-mainText' : 'text-textTertiary'} text-sm`}>
              4. Assign Groups
            </p>
            {selectedPage === 'groups' && <div className="h-0.5 bg-mainText rounded-t-lg mt-2.5" />}
          </div>
          <div>
            <p className={`${selectedPage === 'summary' ? 'text-mainText' : 'text-textTertiary'} text-sm`}>
              5. Summary
            </p>
            {selectedPage === 'summary' && <div className="h-0.5 bg-mainText rounded-t-lg mt-2.5" />}
          </div>
        </div>
        {selectedPage === 'details' && (
          <PolicyDetails setIsNextEnabled={setIsNextEnabled} policyDetails={policyDetails} />
        )}
        {selectedPage === 'rules' && (
          <CreatePolicyRules
            setIsNextEnabled={setIsNextEnabled}
            onPoliciesSelected={setPolicyRules}
            policyRules={policyRules}
          />
        )}
        {selectedPage === 'integration' && (
          <PolicyActions onActionDetailsSelected={setPolicyActionDetails} existingActionDetails={policyActionDetails} />
        )}
        {selectedPage === 'groups' && <PolicyGroups checkedItems={checkedItems} setCheckedItems={setCheckedItems} />}
        {selectedPage === 'summary' && (
          <PolicySummary
            policyDetails={policyDetails}
            policyRules={policyRules}
            policyActionDetails={policyActionDetails}
            activateOnCreate={activateOnCreate}
            setActivateOnCreate={setActivateOnCreate}
            selectedGroups={selectedGroups}
          />
        )}
        <PolicyFooter
          selectedPage={selectedPage}
          setSelectedPage={setSelectedPage}
          onNext={onNextAction}
          disableNext={!isNextEnabled}
        />
      </div>
    </FormProvider>
  )
}

export default CreatePolicyWrapper
